import { useEffect, useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { companion } from "@/context/CompanionAuthContext";
import { useCompanionAuth } from "@/context/CompanionAuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const SERVICES = [
  "External Render & Insulation",
  "Domestic Builds & Extensions",
  "Commercial Construction",
  "Landscaping & Hard-Surfacing",
  "Other",
];

const CompanionNewJob = () => {
  const { user, ready } = useCompanionAuth();
  const navigate = useNavigate();
  const [crew, setCrew] = useState([]);
  const [form, setForm] = useState({
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    address: "",
    postcode: "",
    description: "",
    service_type: SERVICES[0],
    scheduled_date: "",
    scheduled_time_start: "08:30",
    estimated_hours: "4",
    assigned_to: [],
    notes: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    companion.get("/crew").then((r) => setCrew(r.data.filter((u) => u.is_active !== false)));
  }, []);

  if (ready && user?.role !== "admin") return <Navigate to="/companion" replace />;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleAssignee = (id) =>
    setForm((f) => ({
      ...f,
      assigned_to: f.assigned_to.includes(id) ? f.assigned_to.filter((x) => x !== id) : [...f.assigned_to, id],
    }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        customer_email: form.customer_email || null,
        estimated_hours: form.estimated_hours ? parseFloat(form.estimated_hours) : null,
      };
      const { data } = await companion.post("/jobs", payload);
      toast.success("Job created.");
      navigate(`/companion/jobs/${data.id}`, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't create job.");
    } finally { setBusy(false); }
  };

  return (
    <div className="px-4 py-5 space-y-4" data-testid="companion-new-job-page">
      <Link to="/companion/jobs" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-crest-green font-medium">
        <ArrowLeft className="h-3.5 w-3.5" /> Back
      </Link>
      <header>
        <p className="text-xs uppercase tracking-[0.22em] text-crest-gold font-medium">Admin</p>
        <h1 className="mt-1 font-display text-2xl font-bold text-crest-navy">New job</h1>
      </header>

      <form onSubmit={submit} className="space-y-4 rounded-lg bg-white border border-border p-5">
        {[
          ["customer_name", "Customer name", true],
          ["customer_phone", "Phone", false],
          ["customer_email", "Email (optional)", false],
          ["address", "Address", true],
          ["postcode", "Postcode", false],
        ].map(([k, label, required]) => (
          <div key={k}>
            <label className="text-xs uppercase tracking-[0.14em] text-crest-navy/70 font-medium">{label}{required ? " *" : ""}</label>
            <Input value={form[k]} onChange={set(k)} required={required} className="mt-2 h-11" data-testid={`new-job-${k}`} />
          </div>
        ))}
        <div>
          <label className="text-xs uppercase tracking-[0.14em] text-crest-navy/70 font-medium">Description *</label>
          <Textarea rows={4} required value={form.description} onChange={set("description")} className="mt-2" data-testid="new-job-description" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.14em] text-crest-navy/70 font-medium">Service type</label>
          <select value={form.service_type} onChange={set("service_type")} className="mt-2 h-11 w-full rounded-md border border-input bg-white px-3 text-sm" data-testid="new-job-service">
            {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-xs uppercase tracking-[0.14em] text-crest-navy/70 font-medium">Date</label>
            <Input type="date" value={form.scheduled_date} onChange={set("scheduled_date")} className="mt-2 h-11" data-testid="new-job-date" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.14em] text-crest-navy/70 font-medium">Start</label>
            <Input type="time" value={form.scheduled_time_start} onChange={set("scheduled_time_start")} className="mt-2 h-11" data-testid="new-job-time" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.14em] text-crest-navy/70 font-medium">~Hours</label>
            <Input type="number" min="0" step="0.5" value={form.estimated_hours} onChange={set("estimated_hours")} className="mt-2 h-11" data-testid="new-job-hours" />
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.14em] text-crest-navy/70 font-medium">Assign crew</label>
          <div className="mt-2 grid grid-cols-2 gap-2">
            {crew.map((c) => (
              <button
                type="button"
                key={c.id}
                onClick={() => toggleAssignee(c.id)}
                className={`px-3 py-2 rounded-md border text-xs font-medium text-left transition-colors ${
                  form.assigned_to.includes(c.id)
                    ? "bg-crest-green text-white border-crest-green"
                    : "bg-white border-border text-crest-navy hover:border-crest-green"
                }`}
                data-testid={`assign-crew-${c.id}`}
              >
                {c.name || c.email}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.14em] text-crest-navy/70 font-medium">Internal notes</label>
          <Textarea rows={2} value={form.notes} onChange={set("notes")} className="mt-2" data-testid="new-job-notes" />
        </div>
        <button type="submit" disabled={busy} className="w-full h-12 inline-flex items-center justify-center gap-2 rounded-md bg-crest-green text-white font-semibold disabled:opacity-60" data-testid="new-job-submit">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          Create job
        </button>
      </form>
    </div>
  );
};

export default CompanionNewJob;
